import ko from "knockout";
import Promise from "bluebird";
import _ from "lodash";
import apiCore from "services/apiCore";
import localLogger from "services/localLogger";
import serverLogger from "services/serverLogger";
import { sessionService } from "services/sessionService";
import authClientService from "services/authClientService";

const principalId = ko.observable(null);
const userLocale = ko.observable(null);
const oAuthError = ko.observable(false);

let authorizations = [];
let namedPermissions = [];

const PERM_KEY = "perm:";
const ROLE_KEY = "role:";

/**`
 * Parses permissions strings based on the passed in key
 * @returns {Array}
 */
function parsePermissions(key, permString) {
    permString = permString.toLowerCase();
    if (permString.indexOf(key) === 0) {
        return permString.split(key)[1];
    }
    return null;
}

/**
 * Returns permissin string for role based permissions
 * @param role
 * @returns {Array}
 */
function getRolePermission(role) {
    return parsePermissions(ROLE_KEY, role);
}

/**
 * Returns permissions key for name based permissions
 * @param permission
 * @returns {Array}
 */
function getNamePermission(permission) {
    return parsePermissions(PERM_KEY, permission);
}

/**
 * Returns an object that contains the permissions themselves and either the role or name based permissions that
 * are associated with them based on the permission prefix.  If there is no permission prefix the name and role permissions
 * are combined and the string is compared to the combined permisions list.
 *
 * If there are no permission prefixes a warn flag will be set in order to show a deprecation warning in the console.
 *
 * @param auth
 * @returns {{auths: Array, permission: (Array|*), warn: boolean}}
 */
function getAuthorizationsAndPermissions(auth) {
    const namePerm = getNamePermission(auth),
        rolePerm = namePerm ? null : getRolePermission(auth),
        auths = namePerm
            ? namePerm
                ? namedPermissions
                : namedPermissions.concat(authorizations)
            : authorizations,
        perm = namePerm || rolePerm || auth,
        warn = !rolePerm && !namePerm;

    return {
        auths: auths,
        permission: perm,
        warn: warn,
    };
}

/**
 * Returns an array of expanded permissions e.g. architect:user:editor,readonly,admin becomes
 * architect:user:editor, architect:user:readonly, and architect:user:admin
 * @param rawPermissions {Array} The array of permissions unexpanded
 * @returns {Array} The array of expanded permissions
 */
function expandPermissionComponents(rawPermissions) {
    const expandedPermissionComponents = [];
    _.forEach(rawPermissions, function (rawPermission) {
        const components = rawPermission.split(",");
        if (components.length > 1) {
            // The first element will contain the root
            const firstPerm = components.shift();
            const lastColonIndex = firstPerm.lastIndexOf(":");
            if (lastColonIndex >= 0) {
                const root = firstPerm.substr(0, lastColonIndex);
                expandedPermissionComponents.push(firstPerm);
                [].push.apply(
                    expandedPermissionComponents,
                    _.map(components, function (component) {
                        return root + ":" + component;
                    })
                );
            } else {
                localLogger.warn(
                    "Invalid component permission format for: " + rawPermission
                );
            }
        } else {
            expandedPermissionComponents.push(rawPermission);
        }
    });

    return expandedPermissionComponents;
}

/**
 * Locally logs a deprecation warning about use of permissions and role prefixes
 * @param permissionString
 */
function logDeprecationMessage(permissionString) {
    const DEPRECATION_MESSAGE =
        "[DEPRECATION WARNING]: The authorization format used is being deprecated.  Please use role:{1} or perm:{1}";
    localLogger.warn(DEPRECATION_MESSAGE.replace(/\{1\}/gi, permissionString));
}

function registerPermissions(user) {
    authorizations = user.roles
        .filter(({ name }) => name)
        .map(({ name }) => name.toLowerCase());

    namedPermissions = expandPermissionComponents(user.permissions).map(
        (perm) => perm.toLowerCase()
    );
}

function trimDivisionFromPermission(permission) {
    const permissionPieces = permission.split(":");
    if (permissionPieces.length > 3) {
        return permissionPieces.slice(0, 3).join(":");
    }
    return permission;
}

async function registerOauthSession() {
    let oAuthToken;

    try {
        oAuthToken = await authClientService.getToken();

        if (!oAuthToken) {
            throw new Error("No OAuth token available.");
        }
    } catch (err) {
        localLogger.error("Unable to load OAuth credentials", err);
        oAuthError(true);
        return Promise.resolve(); //let the app continue, and display the error
    }

    sessionService.isLoggedIn(true);
    sessionService.isSessionValid(true);
    serverLogger.isLoggedIn(true);

    return apiCore.v2
        .get("/users/me?expand=authorization")
        .then(function (user) {
            registerPermissions(user.authorization);
            /*
                        principalId should always be set last. It's the official indicator that the user is logged in and the
                        application can begin making requests, checking permissions, etc. That means it's essential that all other
                        session data be set up before it is publicly registered.

                        Having the principalId before the above steps could cause some pieces of the app to not load
                        correctly.
                    */
            principalId(user.id);
            localLogger.debug("Logged in with user", user.id, user);
            return oAuthToken;
        });
}

const init = _.memoize(() => {
    return registerOauthSession().catch((err) => {
        localLogger.error("Failed to initialize authentication state", err);
    });
});

const deprecationWarnings = function (permissionData) {
    const deprecationWarning =
        permissionData.warn || _.find(permissionData, { warn: true });

    if (deprecationWarning) {
        logDeprecationMessage(deprecationWarning.permission);
    }
};

/**
 * Authentication service. Handles user login,
 * storing information about the logged in user
 * and the user's authenticaiton token.
 * @exports services/authService
 */
const authService = {
    init,

    /**
     * Flag indicating principalId has been set (indicating that both authorizations and user id are ready for appropriate permissions checking)
     *
     * @returns {boolean} Ensures that principalId has a truthy value
     */
    isLoggedIn: ko.computed(function () {
        return !!principalId();
    }),

    /**
     * Get the user id for the currently logged in user.
     *
     * @returns {observable} obervable for the logged in user's id.
     */
    principalId: ko.computed(function () {
        return principalId();
    }),

    /**
     * Flag to indicated if we've encountered an OAuth error and should show the error screen in the embedded view.
     */
    oAuthError: ko.computed(function () {
        return oAuthError();
    }),

    /**
     * The IETF locale id for the currently logged in user.
     * @returns {string} Either the locale ( en-US, fr-FR, etc ) or null if no user is logged in yet.
     */
    userLocale: ko.computed(function () {
        return userLocale();
    }),

    /**
     * Checks to see if a user has a given authorization.
     *
     * @param {string|object|array} authorizations      The authorizations to check for
     *      If string or array, it is assumed that the required check is "any"
     * @param {array} authorizations.any                Permissions that the user must have at least one of (OR)
     * @param {array} authorizations.all                Permissions that the user must have all of (AND)
     * @param {boolean} authorizations.ignoreDivisions  Same as below
     * @param {boolean} [ignoreDivisions]               Whether to ignore division checks
     *      Has no effect if the ACL toggle is off or a permission is not scoped
     *
     * @returns {boolean} true if the user has the authorization, false otherwise
     */
    hasAuthorization: function (authorizations, ignoreDivisions) {
        // we will convert the currently expected single string or array of strings into the new signature
        if (_.isString(authorizations)) {
            authorizations = {
                any: [authorizations],
            };
        } else if (_.isArray(authorizations)) {
            if (_.every(authorizations, _.isString)) {
                authorizations = {
                    any: authorizations,
                };
            } else if (
                authorizations.length === 1 &&
                (authorizations[0].all || authorizations[0].any)
            ) {
                authorizations = authorizations[0];
            } else {
                localLogger.error(
                    "unexpected hasAuthorization call " +
                        JSON.stringify(authorizations, null, 4)
                );
            }
        }

        const getAuths = function (auths) {
            const permissionsData = _.map(
                auths,
                getAuthorizationsAndPermissions
            );

            deprecationWarnings(permissionsData);

            return permissionsData;
        };

        let hasAny = false;
        let hasAll = false;
        let authData;

        ignoreDivisions = authorizations.ignoreDivisions || ignoreDivisions;

        // if 'any' is not defined or empty, hasAny will be set to true
        if (authorizations.any && !_.isEmpty(authorizations.any)) {
            authData = getAuths(authorizations.any);

            hasAny = _.some(authData, function (permData) {
                if (ignoreDivisions) {
                    return _.some(permData.auths, function (perm) {
                        const trimmedAuth = trimDivisionFromPermission(perm);
                        const trimmedPerm = trimDivisionFromPermission(
                            permData.permission.toLowerCase()
                        );
                        return trimmedAuth === trimmedPerm;
                    });
                }
                return _.includes(
                    permData.auths,
                    permData.permission.toLowerCase()
                );
            });
        } else {
            hasAny = true;
        }

        // if 'all' is not defined or empty, hasAll will be set to true
        if (authorizations.all && !_.isEmpty(authorizations.all)) {
            authData = getAuths(authorizations.all);

            hasAll = _.every(authData, function (permData) {
                if (ignoreDivisions) {
                    return _.some(permData.auths, function (perm) {
                        const trimmedAuth = trimDivisionFromPermission(perm);
                        const trimmedPerm = trimDivisionFromPermission(
                            permData.permission.toLowerCase()
                        );
                        return trimmedAuth === trimmedPerm;
                    });
                }
                return _.includes(
                    permData.auths,
                    permData.permission.toLowerCase()
                );
            });
        } else {
            hasAll = true;
        }

        // every set must pass entirely
        return hasAny && hasAll;
    },

    /**
     * Checks to see if a user has any of the provided authorizations.
     * @param {array} authorizations the list of authorizations to check for
     * @returns {boolean} true if the user has any of the checkedAuthorizations, false otherwise
     */
    hasAnyAuthorization: function (authorizations) {
        //var warning = "[DEPRECATION WARNING]: hasAnyAuthorization is being deprecated.  Use hasAuthorization({ any: ['my', 'permission', 'strings'] }) as a direct replacement";
        //localLogger.warn(warning);

        // handle edge case where concat is used to bundle the original array of strings alongside new object signature
        const authorizationStrings = [];
        const authorizationObjects = [];
        _.each(authorizations, function (auth) {
            if (_.isString(auth)) {
                authorizationStrings.push(auth);
            } else {
                authorizationObjects.push(auth);
            }
        });

        const anyAuthStrings = authService.hasAuthorization({
            any: authorizationStrings,
        });
        const anyAuthObjects = _.some(
            authorizationObjects,
            authService.hasAuthorization
        );

        const hasAuthStrings = !_.isEmpty(authorizationStrings);
        const hasAuthObjects = !_.isEmpty(authorizationObjects);
        const noAuthsDefined = !hasAuthStrings && !hasAuthObjects;

        return (
            noAuthsDefined ||
            (hasAuthStrings && anyAuthStrings) ||
            (hasAuthObjects && anyAuthObjects)
        );
    },

    /**
     * returns a list of all roles in the organization
     * @param {object} query
     * @returns {object} A promise object to handle the asynchronous response from the server
     */
    getRoles: function (query) {
        return apiCore.v2
            .get("/authorization/roles", query)
            .then(function (response) {
                return response.entities;
            });
    },

    /**
     * returns a list of all users granted a role in the organization
     * @param {String} roleId
     * @param {object} options
     * @returns {object} A promise object to handle the asynchronous response from the server
     */
    getRoleSubjectGrants: function (roleId, options) {
        return apiCore.v2
            .get("/authorization/roles/" + roleId + "/subjectgrants", options)
            .then(function (response) {
                return response;
            });
    },

    /** Get the division ids for these perms that you have access to
     * @param {string|array} perms
     */
    getAuthorizedDivisions: function (perms) {
        if (typeof perms === "string") {
            perms = [perms];
        }
        perms = _.map(perms, function (p) {
            return p.toLowerCase();
        });

        function getDivisionId(auth, perm) {
            if (_.startsWith(auth, perm)) {
                const divisionId = auth.substr(perm.length + 1);
                if (/^[a-zA-Z0-9-]{36}$/.exec(divisionId)) {
                    return divisionId;
                }
            }
        }

        const allAuthorizations = namedPermissions.concat(authorizations);
        const divisionIds = {}; // handle duplicates
        _.each(allAuthorizations, function (auth) {
            _.each(perms, function (perm) {
                const divisionId = getDivisionId(auth, perm);
                if (divisionId) {
                    divisionIds[divisionId] = true;
                }
            });
        });
        return _.keys(divisionIds);
    },

    getRawPermissions: function () {
        return apiCore.v2
            .get("/users/me?expand=authorization")
            .then(function (response) {
                return response.authorization.permissions;
            });
    },

    /**
     * Get the current auth token using the new auth client
     * @returns {Promise<string>} The current auth token
     */
    getToken: function () {
        return authClientService.getToken();
    },

    /**
     * Notify about a 401 response due to token expiration
     * @param {string} expiredToken - The token that was used in the failing request
     */
    notify401Response: function (expiredToken) {
        authClientService.notify401Response(expiredToken);
    },
};

export default authService;
